import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useResizeOnNextFrame } from 'lib/hooks/useResizeOnNextFrame';

import { MORE_BUTTON_FALLBACK_WIDTH, splitItemsToFit } from '../../MoreMenu/lib/fitItems';

const INITIAL_METRICS = {
  availableWidth: 0,
  gap: 0,
  chromeWidth: 0,
  moreWidth: MORE_BUTTON_FALLBACK_WIDTH,
  widths: []
};

const toPx = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const areMetricsEqual = (a, b) =>
  a.availableWidth === b.availableWidth &&
  a.gap === b.gap &&
  a.chromeWidth === b.chromeWidth &&
  a.moreWidth === b.moreWidth &&
  a.widths.length === b.widths.length &&
  a.widths.every((width, index) => width === b.widths[index]);

const getAvailableWidth = (parent) => {
  if (!parent) return 0;

  const style = window.getComputedStyle(parent);
  // clientWidth is always 0 on inline elements; offsetWidth still reports a usable box.
  const outerWidth = parent.clientWidth || parent.offsetWidth;

  return Math.max(outerWidth - toPx(style.paddingLeft) - toPx(style.paddingRight), 0);
};

/**
 * Measures the real rendered width of every item and returns the visible/hidden split.
 *
 * @param {Object} params
 * @param {Array} params.items - The full item list, in display order.
 * @param {string} [params.activeValue] - Value of the active item; never collapsed.
 * @param {string} params.signature - Changes whenever any item's rendered width can change.
 * @param {boolean} [params.enabled] - When false, measuring is skipped entirely.
 */
const useOverflowItems = ({ items, activeValue, signature, enabled = true }) => {
  const containerRef = useRef(null);
  const widthsRef = useRef(new Map());
  const moreWidthRef = useRef(0);
  const signatureRef = useRef(signature);
  const measureRef = useRef(() => {});

  const [metrics, setMetrics] = useState(INITIAL_METRICS);
  const [renderedSignature, setRenderedSignature] = useState(signature);

  if (enabled && renderedSignature !== signature) {
    setRenderedSignature(signature);
    setMetrics((previous) => (previous.widths.length ? { ...previous, widths: [] } : previous));
  }

  const split = useMemo(() => {
    if (!enabled) return { visibleItems: items, hiddenItems: [] };

    return splitItemsToFit({
      items,
      activeValue,
      itemWidths: metrics.widths,
      availableWidth: metrics.availableWidth,
      gap: metrics.gap,
      chromeWidth: metrics.chromeWidth,
      moreWidth: metrics.moreWidth
    });
  }, [enabled, items, activeValue, metrics]);

  const measure = ({ visibleItems, hiddenItems }) => {
    const container = containerRef.current;
    if (!enabled || !container) return;

    if (signatureRef.current !== signature) {
      signatureRef.current = signature;
      widthsRef.current.clear();
    }

    const children = container.children;

    visibleItems.forEach((item, index) => {
      const width = Math.ceil(children[index]?.getBoundingClientRect().width ?? 0);
      if (width) widthsRef.current.set(item.value, width);
    });

    if (hiddenItems.length && children.length > visibleItems.length) {
      const width = Math.ceil(children[visibleItems.length].getBoundingClientRect().width);
      if (width) moreWidthRef.current = width;
    }

    const style = window.getComputedStyle(container);

    const next = {
      availableWidth: getAvailableWidth(container.parentElement),
      gap: toPx(style.columnGap),
      chromeWidth:
        toPx(style.paddingLeft) +
        toPx(style.paddingRight) +
        toPx(style.borderLeftWidth) +
        toPx(style.borderRightWidth),
      moreWidth: moreWidthRef.current || MORE_BUTTON_FALLBACK_WIDTH,
      widths: items.map((item) => widthsRef.current.get(item.value) ?? 0)
    };

    setMetrics((previous) => (areMetricsEqual(previous, next) ? previous : next));
  };

  // Runs on every commit, and assigns measureRef in the commit phase only, so observers
  // never read a split that React rendered but threw away. Idempotent, so it settles.
  useLayoutEffect(() => {
    measureRef.current = () => measure(split);
    measureRef.current();
  });

  const remeasure = useCallback(() => measureRef.current(), []);
  useResizeOnNextFrame(containerRef, remeasure, { enabled, observeParent: true });

  useLayoutEffect(() => {
    if (!enabled) return;
    document.fonts?.ready.then(() => measureRef.current()).catch(() => {});
  }, [enabled]);

  return { containerRef, ...split };
};

export default useOverflowItems;
